import { createListenerMiddleware } from '@reduxjs/toolkit'
import { logger } from '#utils/logger'
import type { RootState } from '../root'
import { setApiMode, type TruckConfigurations } from '../slices/configurationSlice'

const log = logger('🔒 AutoLock:')
export const autolockConfiguration = () => {
  const middleware = createListenerMiddleware<RootState>({})
  middleware.startListening({
    predicate: (_action, currentState, previousState) => {
      const entries = Object.keys(currentState.truckConfigurations.configurations) as (keyof TruckConfigurations['configurations'])[]
      for (const entry of entries) {
        const current = currentState.truckConfigurations.configurations[entry]
        const previous = previousState.truckConfigurations.configurations[entry]

        if (current?.lastApiInteraction !== previous?.lastApiInteraction) {
          return true
        }

        // the effect causes a dispatch to change apiConfiguration. To rerun the timer
        // until the next expiry time is reached, it can be checked if the mode has changed
        if (current?.apiConfiguration.mode !== previous?.apiConfiguration.mode) {
          return true
        }
      }
      return false
    },
    effect: async (_action, listenerApi) => {
      listenerApi.cancelActiveListeners()
      let nextTimestamp: Timestamp
      {
        const currentState = listenerApi.getState()
        const expiryTime = currentState.truckConfigurations.meta.sessionExpiryInMs

        const uuids = Object.keys(currentState.truckConfigurations.configurations) as (keyof TruckConfigurations['configurations'])[]
        const entries = uuids
          .map((x) => currentState.truckConfigurations.configurations[x])
          .filter((x) => !!x)
          .filter((x) => x.apiConfiguration.mode !== 'read-only')
        const timestamps = entries.map((x) => x.lastApiInteraction)
        nextTimestamp = (Math.min(...timestamps) + expiryTime) as Timestamp
      }

      log.debug(`Next update required at ${nextTimestamp}`)

      await waitUntil(nextTimestamp)
      {
        // block in place to not accidentially mistake the two st
        // should _not_ drift, but might be
        const now = Date.now() + 5
        const newState = listenerApi.getState()
        const expiryTime = newState.truckConfigurations.meta.sessionExpiryInMs
        const newUuids = Object.keys(newState.truckConfigurations.configurations) as (keyof TruckConfigurations['configurations'])[]
        const expiredEntries = newUuids
          .map((x) => newState.truckConfigurations.configurations[x])
          .filter((x) => !!x)
          .filter((x) => x.lastApiInteraction + expiryTime < now)
        for (const expiredEntry of expiredEntries) {
          log.debug(`flagging R/O for ${expiredEntry.uuid} (${expiredEntry.title})`)
          listenerApi.dispatch(setApiMode({ id: expiredEntry.id, mode: 'read-only' }))
        }
      }
    },
  })

  return middleware
}

const waitUntil = (timestamp: number) => {
  return new Promise<void>((resolve) => {
    const millisecondsToTimestamp = timestamp - Date.now()
    setInterval(() => {
      resolve()
    }, millisecondsToTimestamp)
  })
}
